import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import { Button, Segmented, StatePanel } from "../../components/ui";
import "./core-list-suggestions.scss";

/**
 * References worth adding, from what the store has actually done
 * (Alex item 5, 2026-10-06).
 *
 * Lives inside the Add Products modal rather than on the page: a suggestion
 * is only useful at the moment you are deciding what to add, and a permanent
 * strip above 116 references is one more thing to scroll past.
 *
 * ⚠️ "HIGH TAT" IS SHOWN AS FASTEST TURNAROUND. Alex asked for "High TAT".
 * TAT is turn-around time, where a high number means SLOW — and a slow mover
 * is the last thing to put on a must-always-have list. The caption states the
 * measure ("median days from purchase to payment") so the number says what it
 * is whichever way the ranking is meant; if the other reading was intended,
 * inverting it is one line in the backend.
 *
 * Every figure is shown next to its reference. A ranked list with no numbers
 * asks you to trust the ranking; with numbers you can see that the top two
 * sold nine and eight, and the next sold one.
 */
const fetchSuggestions = async (storeIdM) => {
  const { data } = await axios.get("/api/core-list/suggestions", {
    params: { store_id_m: storeIdM },
  });
  return data;
};

const CoreListSuggestions = ({ storeIdM, onAdd, addingIds = [] }) => {
  const [group, setGroup] = useState("most_sold");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["core-list-suggestions", storeIdM],
    queryFn: () => fetchSuggestions(storeIdM),
    enabled: storeIdM != null,
    staleTime: 300_000,
  });

  const groups = data?.groups || [];
  const active = groups.find((g) => g.key === group) || groups[0];

  if (isError) return null; // A suggestion failing is not worth an error state.

  return (
    <section className="cl-sugg">
      <div className="cl-sugg__head">
        <span className="cl-sugg__title">Suggested from your own history</span>
        {groups.length > 0 && (
          <Segmented
            size="sm"
            options={groups.map((g) => ({ value: g.key, label: g.label }))}
            value={active?.key}
            onChange={setGroup}
            aria-label="Suggestion grouping"
          />
        )}
      </div>

      {isLoading ? (
        <StatePanel state="loading" compact title="Reading your sales…" />
      ) : !active?.references?.length ? (
        <StatePanel
          state="empty"
          compact
          title="Nothing to suggest yet"
          description="Suggestions come from your own sales and leads over the last 12 months. References already on your Core List are left out."
        />
      ) : (
        <>
          <p className="cl-sugg__caption">{active.caption}</p>
          <ul className="cl-sugg__list">
            {active.references.map((ref) => (
              <li className="cl-sugg__item" key={ref.product_id}>
                <div className="cl-sugg__ref">
                  <span className="cl-sugg__reference">{ref.reference}</span>
                  <span className="cl-sugg__name">
                    {ref.brand} · {ref.model}
                  </span>
                </div>

                <div className="cl-sugg__figure">
                  <span className="cl-sugg__value">{ref.value}</span>
                  <span className="cl-sugg__unit">
                    {active.unit}
                    {/* Two sales and twenty sales are different claims, so a
                        median says how many it rests on. */}
                    {ref.sample ? ` · ${ref.sample} sales` : ""}
                  </span>
                </div>

                <Button
                  size="sm"
                  variant="secondary"
                  disabled={addingIds.includes(ref.product_id)}
                  onClick={() => onAdd?.(ref.product_id)}
                >
                  {addingIds.includes(ref.product_id) ? "Added" : "Add"}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

export default CoreListSuggestions;
